import {Component, type ErrorInfo, type ReactNode} from 'react'
import * as Sentry from '@sentry/react'
import {useTranslation} from 'react-i18next'
import {KairosMark} from '@/components/kairos-mark'
import {Button} from '@/components/ui/button'
// Se llama UPGRADE_ por su primer uso, pero es el único buzón público del producto.
import {UPGRADE_CONTACT_EMAIL} from '@/lib/constants'
import casimiroUps from '@/assets/casimiro/casimiro-ups.webp'

/** Vive fuera del router (la monta `main.tsx`): si lo que se rompió fue el router, `navigate`
 * también fallaría, así que las acciones son navegación del navegador. */
function CrashScreen() {
  const {t} = useTranslation()
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="surface surface-raised w-full max-w-2xl overflow-hidden">
        <div className="relative flex h-44 items-center justify-center overflow-hidden bg-ink-deep text-ink-deep-foreground sm:h-52">
          <div aria-hidden="true" className="cover-ruled absolute inset-0" />
          <img src={casimiroUps} alt={t('crash.casimiroAlt')} decoding="async" className="relative h-full w-auto" />
        </div>
        <div className="p-8 text-center">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <KairosMark />
            <p className="font-mono text-[10px] font-medium tracking-[0.12em] uppercase">{t('crash.eyebrow')}</p>
          </div>
          <h1 className="mt-3 font-heading text-2xl text-foreground">{t('crash.title')}</h1>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">{t('crash.body')}</p>
          <div className="mt-7 flex flex-col items-center justify-center gap-2 sm:flex-row">
            <Button size="lg" data-testid="crash-reload" onClick={() => window.location.reload()}>
              {t('crash.reload')}
            </Button>
            <Button variant="outline" size="lg" data-testid="crash-home" onClick={() => window.location.assign('/')}>
              {t('crash.backHome')}
            </Button>
          </div>
          <p className="mt-5 text-xs text-muted-foreground">
            <a
              className="underline underline-offset-2 hover:text-foreground"
              href={`mailto:${UPGRADE_CONTACT_EMAIL}?subject=${encodeURIComponent('Un error en Kairos')}`}>
              {t('crash.contact')}
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

/** Reporta a Sentry con el stack de componentes, que un stack de JS minificado no trae. */
export class ErrorBoundary extends Component<{children: ReactNode}, {crashed: boolean}> {
  state = {crashed: false}

  static getDerivedStateFromError(): {crashed: boolean} {
    return {crashed: true}
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    Sentry.captureException(error, {extra: {componentStack: info.componentStack}})
  }

  render(): ReactNode {
    return this.state.crashed ? <CrashScreen /> : this.props.children
  }
}
